import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder, faFolderOpen, faFolderPlus, faPen } from "@fortawesome/free-solid-svg-icons";
import { faArrowLeft, faBars, faLayerGroup, faTriangleExclamation, faLink, faLinkSlash } from "@fortawesome/free-solid-svg-icons";
import styles from "../WorkoutAdminModal.module.css";
import Badge from "./Badge";
import PlanItemRow from "./PlanItemRow";
import { buildItemBlocks, groupLabel } from "./workoutAdminHelpers";
import { useWorkoutAdminContext } from "../../context/WorkoutAdminContext";
import { useWorkoutPrograms } from "../../hooks/useWorkoutPrograms";
import ProgramModal from "./ProgramModal";

// Props removidas em favor de useWorkoutAdminContext() (issue 012,
// docs/07-issues.md) -- mesmo WorkoutAdminContext já usado por StudentsTab.jsx
// e StudentAssignmentModal.jsx. visibleStudents/selectedStudentIds/
// toggleStudentSelection/selectedStudents/assignForm/setAssignForm/
// handleAssignPlan foram removidas de vez (não só do contexto): só eram
// usadas dentro de um bloco JSX morto ({false && (...)} -- "Vincular treino
// aos alunos", achado durante essa mesma limpeza, igual ao StudentsTab.jsx).
export default function PlansTab() {
  const {
    filteredPlans,
    listablePlans,
    selectedPlanId,
    setSelectedPlanId,
    selectedPlan,
    resetPlanItemEditor,
    planQuery,
    setPlanQuery,
    isStandardPlan,
    isBlankPlan,
    professorsById,
    openCreatePlanModal,
    canManageSelectedPlan,
    itemsExpanded,
    setItemsExpanded,
    isMobile,
    openCreatePlanItemModal,
    openEditPlanModal,
    handleDeletePlan,
    saving,
    openPlanActionsMenu,
    setOpenPlanActionsMenu,
    groupSelectMode,
    setGroupSelectMode,
    groupSelectedIds,
    groupConflicts,
    handleCreateGroupClick,
    groupSelectionType,
    handleCancelGroupSelect,
    handleMoveBlock,
    handleUngroupBlock,
    toggleGroupSelectItem,
    openEditPlanItemModal,
    handleDeletePlanItem,
  } = useWorkoutAdminContext();

  // Programas: pastas que agrupam os treinos de um método, na ordem em que o
  // aluno faz (A, B, C). Com busca ativa a lista volta a ser plana.
  const { programs, saveProgram, deleteProgram, reload: reloadPrograms } = useWorkoutPrograms();
  // Treino excluído (ou criado) muda a lista de treinos: recarrega os
  // programas pra contagem "N treinos" da pasta não ficar desatualizada.
  const planIdsKey = listablePlans.map((p) => p.id).join(",");
  const firstPlanIdsKey = useRef(true);
  useEffect(() => {
    if (firstPlanIdsKey.current) { firstPlanIdsKey.current = false; return; }
    reloadPrograms();
  }, [planIdsKey, reloadPrograms]);
  const [programModal, setProgramModal] = useState(null); // null | "new" | programa
  // pastas começam fechadas; abre a do treino selecionado
  const [expandedPrograms, setExpandedPrograms] = useState(() => new Set());

  const programOfPlan = useMemo(() => {
    const map = new Map();
    programs.forEach((prog) => (prog.plans || []).forEach((p) => map.set(p.id, prog)));
    return map;
  }, [programs]);

  const planGroups = useMemo(() => {
    if (planQuery.trim() || !programs.length) return null;
    const byId = new Map(filteredPlans.map((p) => [p.id, p]));
    const folders = programs.map((prog) => ({
      program: prog,
      plans: (prog.plans || []).map((p) => byId.get(p.id)).filter(Boolean),
    }));
    const loose = filteredPlans.filter((p) => !programOfPlan.has(p.id));
    return { folders, loose };
  }, [planQuery, programs, filteredPlans, programOfPlan]);

  const selectedProgramId = selectedPlanId ? programOfPlan.get(selectedPlanId)?.id : null;
  useEffect(() => {
    if (!selectedProgramId) return;
    setExpandedPrograms((prev) => (prev.has(selectedProgramId) ? prev : new Set(prev).add(selectedProgramId)));
  }, [selectedProgramId]);

  const toggleProgram = (id) => {
    setExpandedPrograms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const programCandidates = (prog) => listablePlans.filter(
    (p) => !isStandardPlan(p) && !isBlankPlan(p) && (!prog || p.professor_id === prog.professor_id)
  );

  const renderPlanButton = (plan, letter) => (
    <button
      key={plan.id}
      type="button"
      className={`${styles.wamPlanItem} ${
        isStandardPlan(plan) ? styles.standardPlanItem : ""
      } ${selectedPlanId === plan.id ? styles.activeItem : ""}`}
      aria-pressed={selectedPlanId === plan.id}
      onClick={() => {
        setSelectedPlanId(plan.id);
        resetPlanItemEditor();
      }}
    >
      <div className={styles.itemTop}>
        <strong>
          {letter && <span className={styles.programLetter} aria-hidden="true" style={{ marginRight: 8 }}>{letter}</span>}
          {plan.title}
        </strong>
        <Badge tone={plan.is_active ? "success" : "muted"}>
          {plan.is_active ? "Ativo" : "Inativo"}
        </Badge>
      </div>

      <div className={styles.metaRow}>
        {!letter && programOfPlan.get(plan.id) && (
          <Badge tone="brand">{programOfPlan.get(plan.id).name}</Badge>
        )}
        {(() => {
          const profName = plan.professor?.name || professorsById.get(plan.professor_id)?.name;
          return profName ? <Badge tone="soft">{profName}</Badge> : null;
        })()}
        {isBlankPlan(plan) ? <Badge tone="warning">Em branco</Badge> : null}
        {isStandardPlan(plan) ? <Badge tone="standard">Padrão</Badge> : null}
      </div>
    </button>
  );

  return (
    <>
      <div className={`${styles.wamGrid} ${selectedPlanId ? styles.hasSelection : ""}`}>
        <div className={styles.wamSidebar}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionHeadLeft}>
              <div className={styles.titleLine}>
                <h3>Treinos</h3>
                <Badge tone="info">{filteredPlans.length}/{listablePlans.length}</Badge>
              </div>
            </div>

            <div className={styles.sectionHeadActions}>
              <button
                type="button"
                className={`${styles.wamBtn} ${styles.primary}`}
                onClick={openCreatePlanModal}
              >
                Criar treino
              </button>
            </div>
          </div>

          <div className={styles.sectionSubline}>
            <input
              className={styles.searchInput}
              type="search"
              placeholder="Buscar treino"
              value={planQuery}
              onChange={(e) => setPlanQuery(e.target.value)}
              aria-label="Buscar treino"
            />
          </div>

          <div className={`${styles.sectionSubline} ${styles.programNewRow}`}>
            <button
              type="button"
              className={`${styles.wamBtn} ${styles.smallGhost}`}
              onClick={() => setProgramModal("new")}
            >
              <FontAwesomeIcon icon={faFolderPlus} style={{ marginRight: 6 }} aria-hidden="true" />
              Novo programa
            </button>
            {!programs.length && (
              <span className={styles.programHint}>Agrupe treinos de um método (ex: PPL)</span>
            )}
          </div>

          <div className={styles.wamList}>
            {planGroups ? (
              <>
                {planGroups.folders.map(({ program, plans }) => {
                  const collapsed = !expandedPrograms.has(program.id);
                  const bodyId = `program-folder-${program.id}`;
                  return (
                    <div key={program.id} className={styles.programFolder}>
                      <div className={styles.programFolderHead}>
                        <button
                          type="button"
                          className={styles.programFolderToggle}
                          aria-expanded={!collapsed}
                          aria-controls={bodyId}
                          onClick={() => toggleProgram(program.id)}
                        >
                          <FontAwesomeIcon icon={collapsed ? faFolder : faFolderOpen} aria-hidden="true" className={styles.programFolderIcon} />
                          <span className={styles.programFolderText}>
                            <span className={styles.programFolderName}>{program.name}</span>
                            <span className={styles.programFolderMeta}>
                              {program.method ? <Badge tone="info">{program.method}</Badge> : null}
                              <span>
                                {(program.plans || []).length} {(program.plans || []).length === 1 ? "treino" : "treinos"}
                                {program.students_in_use > 0 && (
                                  <> • {program.students_in_use} {program.students_in_use === 1 ? "aluno" : "alunos"}</>
                                )}
                              </span>
                            </span>
                          </span>
                        </button>
                        <button
                          type="button"
                          className={styles.programFolderEdit}
                          onClick={() => setProgramModal(program)}
                          aria-label={`Editar programa ${program.name}`}
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                      </div>
                      {!collapsed && (
                        <div id={bodyId} className={styles.programFolderBody}>
                          {plans.map((plan) => {
                            const pos = (program.plans || []).findIndex((p) => p.id === plan.id);
                            return renderPlanButton(plan, "ABCDEFGHIJKLMN"[pos] || String(pos + 1));
                          })}
                          {!plans.length && (
                            <p className={styles.programHint}>Nenhum treino neste programa.</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                {planGroups.loose.length > 0 && (
                  <div className={styles.programFolderHead}>
                    <span className={styles.programHint}>Treinos avulsos</span>
                  </div>
                )}
                {planGroups.loose.map((plan) => renderPlanButton(plan, null))}
              </>
            ) : (
              filteredPlans.map((plan) => renderPlanButton(plan, null))
            )}

            {!filteredPlans.length && (
              <div className={styles.wamEmpty}>Nenhum treino encontrado.</div>
            )}
          </div>
        </div>

        <div className={styles.wamMain}>
          {selectedPlan && (
            <div className={styles.wamMobileBackBar}>
              <button
                type="button"
                className={styles.wamMobileBackIcon}
                aria-label="Voltar para treinos"
                onClick={() => { setSelectedPlanId(null); resetPlanItemEditor(); }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <strong>{selectedPlan.title}</strong>
              <button
                type="button"
                className={styles.wamMobileSwitchBtn}
                onClick={() => { setSelectedPlanId(null); resetPlanItemEditor(); }}
              >
                Trocar
              </button>
            </div>
          )}
          <div className={styles.wamStack}>
            {selectedPlan && (
              <>
                <div className={styles.cardGlass}>
                  <div className={styles.sectionHead}>
                    <div className={styles.sectionHeadLeft}>
                      <h4>1. Monte o treino</h4>
                      <div className={styles.metaRow}>
                        <Badge tone="default">{selectedPlan.title}</Badge>
                        {(() => {
                          const prog = programOfPlan.get(selectedPlan.id);
                          if (!prog) return null;
                          const pos = (prog.plans || []).findIndex((x) => x.id === selectedPlan.id);
                          return <Badge tone="brand">{`${prog.name} - treino ${"ABCDEFGHIJKLMN"[pos] || pos + 1}`}</Badge>;
                        })()}
                        {(() => {
                          const profName = selectedPlan.professor?.name || professorsById.get(selectedPlan.professor_id)?.name;
                          return profName ? <Badge tone="soft">{profName}</Badge> : null;
                        })()}
                        {isBlankPlan(selectedPlan) ? <Badge tone="warning">Em branco</Badge> : null}
                        {isStandardPlan(selectedPlan) ? <Badge tone="standard">Padrão</Badge> : null}
                      </div>
                    </div>

                    {!isMobile ? (
                      <div className={styles.actionsRow}>
                        <button
                          type="button"
                          className={`${styles.wamBtn} ${styles.ghost} ${styles.wamBtnSm}`}
                          onClick={() => setItemsExpanded((prev) => !prev)}
                        >
                          {itemsExpanded ? "Ocultar itens" : "Ver itens"}
                        </button>

                        {canManageSelectedPlan && (
                        <button
                          type="button"
                          className={`${styles.wamBtn} ${styles.primary} ${styles.wamBtnSm}`}
                          onClick={openCreatePlanItemModal}
                        >
                          Incluir exercício
                        </button>
                        )}

                        {canManageSelectedPlan && (<>
                        <button
                          type="button"
                          className={`${styles.wamBtn} ${styles.ghost} ${styles.wamBtnSm}`}
                          onClick={() => openEditPlanModal(selectedPlan)}
                        >
                          Editar treino
                        </button>
                        {!selectedPlan.is_blank && (
                        <button
                          type="button"
                          className={`${styles.wamBtn} ${styles.danger} ${styles.wamBtnSm}`}
                          onClick={() => handleDeletePlan(selectedPlan.id)}
                          disabled={saving}
                        >
                          Excluir
                        </button>
                        )}
                        </>)}
                      </div>
                    ) : (
                      <div className={styles.mobileActionsMenuWrap}>
                        <button
                          type="button"
                          className={styles.mobileMenuButton}
                          onClick={() => setOpenPlanActionsMenu((prev) => !prev)}
                          aria-label="Abrir ações do treino"
                        >
                          <FontAwesomeIcon icon={faBars} />
                        </button>

                        {openPlanActionsMenu && (
                          <div className={styles.mobileActionsDropdown}>
                            <button
                              type="button"
                              className={styles.mobileActionsItem}
                              onClick={() => {
                                setItemsExpanded((prev) => !prev);
                                setOpenPlanActionsMenu(false);
                              }}
                            >
                              {itemsExpanded ? "Ocultar itens" : "Ver itens"}
                            </button>

                            {canManageSelectedPlan && (
                            <button
                              type="button"
                              className={styles.mobileActionsItem}
                              onClick={() => {
                                openCreatePlanItemModal();
                                setOpenPlanActionsMenu(false);
                              }}
                            >
                              Incluir exercício
                            </button>
                            )}

                            {canManageSelectedPlan && (<>
                            <button
                              type="button"
                              className={styles.mobileActionsItem}
                              onClick={() => {
                                openEditPlanModal(selectedPlan);
                                setOpenPlanActionsMenu(false);
                              }}
                            >
                              Editar treino
                            </button>
                            {!selectedPlan.is_blank && (
                            <button
                              type="button"
                              className={`${styles.mobileActionsItem} ${styles.mobileActionsDanger}`}
                              onClick={() => {
                                handleDeletePlan(selectedPlan.id);
                                setOpenPlanActionsMenu(false);
                              }}
                              disabled={saving}
                            >
                              Excluir
                            </button>
                            )}
                            </>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className={styles.planBuilderStack}>
                    <div className={styles.cardInner}>
                      <button
                        type="button"
                        className={styles.planItemsToggle}
                        onClick={() => setItemsExpanded((prev) => !prev)}
                      >
                        <div className={styles.planItemsToggleLeft}>
                          <h4>Itens adicionados</h4>
                          <Badge tone="soft">{selectedPlan.items?.length || 0}</Badge>
                        </div>
                        <span
                          className={`${styles.chevron} ${
                            itemsExpanded ? styles.chevronOpen : ""
                          }`}
                        >
                          ▾
                        </span>
                      </button>

                      {itemsExpanded && canManageSelectedPlan && (selectedPlan.items?.length || 0) >= 2 && (
                        <div className={styles.groupSelectBar}>
                          {!groupSelectMode ? (
                            <button
                              type="button"
                              className={`${styles.wamBtn} ${styles.smallGhost}`}
                              onClick={() => setGroupSelectMode(true)}
                            >
                              <FontAwesomeIcon icon={faLayerGroup} /> Agrupar em bi-set
                            </button>
                          ) : (
                            <>
                              <span className={styles.groupSelectHint} aria-live="polite">
                                {groupSelectedIds.length === 0
                                  ? "Selecione 2+ exercícios pra formar um bi-set/superset"
                                  : `${groupSelectedIds.length} selecionado(s)`}
                              </span>
                              {groupConflicts.length > 0 && (
                                <span className={styles.groupConflictWarning} role="alert">
                                  <FontAwesomeIcon icon={faTriangleExclamation} /> Vai desfazer o grupo com{" "}
                                  {groupConflicts.map((c) => c.names.join(" + ")).join("; ")}
                                </span>
                              )}
                              <button
                                type="button"
                                className={`${styles.wamBtn} ${styles.smallGhost}`}
                                onClick={handleCreateGroupClick}
                                disabled={groupSelectedIds.length < 2 || saving}
                              >
                                {groupSelectionType
                                  ? `Agrupar (${groupSelectedIds.length}) em ${groupSelectionType.toLowerCase()}`
                                  : "Agrupar"}
                              </button>
                              <button
                                type="button"
                                className={`${styles.wamBtn} ${styles.smallGhost}`}
                                onClick={handleCancelGroupSelect}
                              >
                                Cancelar
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      {itemsExpanded && (
                        <div className={styles.planItemList}>
                          {buildItemBlocks(
                            [...(selectedPlan.items || [])].sort(
                              (a, b) => (a.order_index || 0) - (b.order_index || 0)
                            )
                          ).map((block, blockIdx, blocksArr) =>
                            block.kind === "group" ? (
                              <div className={styles.groupWrapper} key={`group-${block.groupId}`}>
                                <div className={styles.groupWrapperHeader}>
                                  <span className={styles.groupWrapperLabel}>
                                    <FontAwesomeIcon icon={faLink} />
                                    {groupLabel(block.items)} · sem descanso entre os exercícios
                                  </span>
                                  {canManageSelectedPlan && !groupSelectMode && (
                                    <div className={styles.groupWrapperActions}>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallGhost}`}
                                        title="Mover grupo para cima"
                                        onClick={() => handleMoveBlock(block, -1)}
                                        disabled={saving || blockIdx === 0}
                                      >
                                        ↑
                                      </button>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallGhost}`}
                                        title="Mover grupo para baixo"
                                        onClick={() => handleMoveBlock(block, 1)}
                                        disabled={saving || blockIdx === blocksArr.length - 1}
                                      >
                                        ↓
                                      </button>
                                      <button
                                        type="button"
                                        className={`${styles.wamBtn} ${styles.smallGhost}`}
                                        onClick={() => handleUngroupBlock(block.groupId)}
                                      >
                                        <FontAwesomeIcon icon={faLinkSlash} /> Desagrupar
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {block.items.map((item) => (
                                  <PlanItemRow
                                    key={item.id}
                                    item={item}
                                    canManage={canManageSelectedPlan}
                                    selectMode={groupSelectMode}
                                    isSelected={groupSelectedIds.includes(item.id)}
                                    onToggleSelect={toggleGroupSelectItem}
                                    onEdit={() => openEditPlanItemModal(item)}
                                    onDelete={() => handleDeletePlanItem(item.id)}
                                    showMoveButtons={false}
                                    saving={saving}
                                  />
                                ))}
                              </div>
                            ) : (
                              <PlanItemRow
                                key={block.item.id}
                                item={block.item}
                                canManage={canManageSelectedPlan}
                                selectMode={groupSelectMode}
                                isSelected={groupSelectedIds.includes(block.item.id)}
                                onToggleSelect={toggleGroupSelectItem}
                                onEdit={() => openEditPlanItemModal(block.item)}
                                onDelete={() => handleDeletePlanItem(block.item.id)}
                                onMoveUp={() => handleMoveBlock(block, -1)}
                                onMoveDown={() => handleMoveBlock(block, 1)}
                                moveUpDisabled={blockIdx === 0}
                                moveDownDisabled={blockIdx === blocksArr.length - 1}
                                showMoveButtons
                                saving={saving}
                              />
                            )
                          )}

                          {!selectedPlan.items?.length && (
                            <div className={styles.wamEmpty}>
                              Nenhum item adicionado neste treino.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </>
            )}

            {!selectedPlan && (
              <div className={styles.cardGlass}>
                <div className={styles.wamEmpty}>
                  Selecione um treino na lateral ou crie um novo para começar.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {programModal && (
        <ProgramModal
          program={programModal === "new" ? null : programModal}
          availablePlans={programCandidates(programModal === "new" ? null : programModal)}
          programOfPlan={programOfPlan}
          onClose={() => setProgramModal(null)}
          onSave={saveProgram}
          onDelete={deleteProgram}
        />
      )}
    </>
  );
}
